import { useEffect, useState } from "react"
import { create } from "zustand"
import { persist } from "zustand/middleware"

/**
 * 이 값은 index.html의 첫 페인트 전 인라인 스크립트에도 리터럴로 박혀 있다.
 * 그 스크립트는 모듈이 하나도 로드되기 전에 돌기 때문에 이 상수를 import할 수
 * 없고, ESLint도 .html 안까지는 못 본다. 이 저장소에서 철자를 하나로 만들 수
 * 없는 유일한 키이므로, 바꾸려면 index.html을 함께 고쳐야 한다.
 */
export const THEME_STORAGE_KEY = "theme-storage"

export enum ThemeEnum {
  Light = "light",
  Dark = "dark",
  System = "system",
}

export type ResolvedTheme = ThemeEnum.Light | ThemeEnum.Dark

const darkMediaQuery = () => window.matchMedia("(prefers-color-scheme: dark)")

export const resolveTheme = (theme: ThemeEnum): ResolvedTheme => {
  if (theme === ThemeEnum.System) {
    return darkMediaQuery().matches ? ThemeEnum.Dark : ThemeEnum.Light
  }
  return theme
}

// index.html의 인라인 스크립트가 첫 페인트 전에 같은 규칙으로 클래스를 적용한다.
// 두 곳의 로직이 어긋나면 새로고침 시 테마가 깜빡이므로 함께 수정할 것.
export const applyTheme = (theme: ThemeEnum) => {
  document.documentElement.classList.toggle(
    "dark",
    resolveTheme(theme) === ThemeEnum.Dark
  )
}

interface ThemeState {
  theme: ThemeEnum
}

interface ThemeAction {
  setTheme: (theme: ThemeEnum) => void
}

export const useThemeStore = create<ThemeState & ThemeAction>()(
  persist(
    (set) => ({
      // 다크모드는 실험적 기능이므로 명시적으로 선택하기 전까지는 라이트를 쓴다.
      theme: ThemeEnum.Light,
      setTheme: (theme) => {
        set({ theme })
        applyTheme(theme)
      },
    }),
    {
      name: THEME_STORAGE_KEY,
    }
  )
)

const readAppliedTheme = (): ResolvedTheme =>
  document.documentElement.classList.contains("dark")
    ? ThemeEnum.Dark
    : ThemeEnum.Light

/**
 * 실제로 적용된 테마를 돌려준다. 저장된 설정(useThemeStore)과 달리 "system"에
 * 머무르지 않고 OS 설정 변화까지 따라간다. 클래스가 붙은 뒤에 갱신되므로,
 * CSS 변수를 직접 읽어야 하는 캔버스 같은 곳에서 최신 값을 보장한다.
 */
export const useResolvedTheme = (): ResolvedTheme => {
  const [resolved, setResolved] = useState<ResolvedTheme>(readAppliedTheme)

  useEffect(() => {
    const sync = () => setResolved(readAppliedTheme())
    sync()

    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })
    return () => observer.disconnect()
  }, [])

  return resolved
}

export const useThemeInit = () => {
  const theme = useThemeStore((state) => state.theme)

  useEffect(() => {
    applyTheme(theme)

    if (theme !== ThemeEnum.System) return

    const mediaQuery = darkMediaQuery()
    const handleChange = () => applyTheme(ThemeEnum.System)
    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [theme])
}
